import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import './Product.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { API_URL } from '../../api/config';


const Product = () => {
  const { userToken: token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters / search
  const [searchQuery, setSearchQuery] = useState('');
  const [stockFilter, setStockFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Form modal states
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  // Image
  const [selectedImage, setSelectedImage] = useState(null);

  // Product form states
  const initialFormData = {
    itemName: '',
    category: '',
    pricingType: 'basePrice',
    basePrice: '',
    mrp: '',
    sellingPrice: '',
    type: '',
    unitType: '',
    variants: []
  };
  const [formData, setFormData] = useState(initialFormData);

  // Options
  const typeOptions = [
    'Beverage', 'Starter', 'Dessert', 'Breads',
    'Main-Course', 'Combo', 'Sweets', 'Snacks', 'Custom'
  ];
  const unitTypes = {
    Size: ['Small', 'Medium', 'Large'],
    Quantity: ['Quarter', 'Half', 'Full'],
    Weight: ['250g', '500g', '1kg'],
    Custom: ['Custom']
  };

  const handleError = useCallback((error, action) => {
    console.error(`Error ${action}:`, error);
    toast.error(`Failed to ${action.toLowerCase()}. Please try again.`);
  }, []);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      if (!token) {
        throw new Error('Authentication token is missing');
      }
      const response = await fetch(`${API_URL}/products`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(
          errorData?.message || `Server responded with status: ${response.status}`
        );
      }
      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Invalid data format received from server');
      }
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error(error?.message || 'Failed to fetch products. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchProducts();
    }
  }, [token, fetchProducts]);

  // Handle file selection for image upload
  const handleImagePick = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImage({
        file,
        uri: URL.createObjectURL(file)
      });
    }
  };

  const handleFormSubmit = async (formValues) => {
    try {
      if (!token) throw new Error('Authentication token is missing');

      const formDataToSend = new FormData();
      Object.keys(formValues).forEach((key) => {
        if (key === 'variants') {
          formDataToSend.append(key, JSON.stringify(formValues[key]));
        } else {
          formDataToSend.append(key, formValues[key]);
        }
      });

      if (selectedImage) {
        // Append the image file
        formDataToSend.append('image', selectedImage.file);
      }

      const url = editingProduct
        ? `${API_URL}/products/${editingProduct._id}`
        : `${API_URL}/products`;

      const response = await fetch(url, {
        method: editingProduct ? 'PUT' : 'POST',
        headers: {
          Authorization: `Bearer ${token}`
          // Do not set Content-Type manually; let the browser set correct boundary for multipart
        },
        body: formDataToSend
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        throw new Error(errorData?.message || 'Failed to save product');
      }

      await fetchProducts();
      resetForm();
      toast.success(`Product ${editingProduct ? 'updated' : 'created'} successfully`);
    } catch (error) {
      handleError(error, 'save product');
    }
  };

  const handleDelete = useCallback(
    async (productId) => {
      if (window.confirm('Are you sure you want to delete this product?')) {
        try {
          if (!token) throw new Error('Authentication token is missing');
          const response = await fetch(`${API_URL}/products/${productId}`, {
            method: 'DELETE',
            headers: {
              Authorization: `Bearer ${token}`,
              Accept: 'application/json',
              'Content-Type': 'application/json'
            }
          });
          if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(errorData?.message || 'Failed to delete product');
          }
          await fetchProducts();
          toast.success('Product deleted successfully');
        } catch (error) {
          handleError(error, 'delete product');
        }
      }
    },
    [token, fetchProducts, handleError]
  );

  const handleStockUpdate = useCallback(
    async (productId, inStock, duration = null) => {
      try {
        if (!token) throw new Error('Authentication token is missing');
        const response = await fetch(`${API_URL}/products/${productId}/stock`, {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ inStock, duration })
        });
        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          throw new Error(errorData?.message || 'Failed to update stock status');
        }
        await fetchProducts();
      } catch (error) {
        handleError(error, 'update stock status');
      }
    },
    [token, fetchProducts, handleError]
  );

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setSelectedImage(null);
    setEditingProduct(null);
    setIsFormVisible(false);
  }, []);

  const handleEdit = useCallback((product) => {
    setEditingProduct(product);
    setFormData({
      itemName: product.itemName || '',
      category: product.category || '',
      pricingType: product.pricingType || 'basePrice',
      basePrice: product.basePrice?.toString() || '',
      mrp: product.mrp?.toString() || '',
      sellingPrice: product.sellingPrice?.toString() || '',
      type: product.type || '',
      unitType: product.unitType || '',
      variants:
        product.variants?.map((variant) => ({
          name: variant.name || '',
          price: variant.price?.toString() || ''
        })) || []
    });
    setSelectedImage(product.imageUrl ? { uri: product.imageUrl } : null);
    setIsFormVisible(true);
  }, []);

  // --- Product Form Component ---
  const ProductForm = () => {
    const [currentFormData, setCurrentFormData] = useState({
      ...formData,
      pricingType: formData.pricingType || 'basePrice',
      mrp: formData.mrp || '',
      sellingPrice: formData.sellingPrice || '',
      variants: formData.variants?.length
        ? formData.variants
        : [{ name: '', price: '' }]
    });
    const [errors, setErrors] = useState({});

    const validateForm = () => {
      const newErrors = {};
      if (!currentFormData.itemName?.trim()) {
        newErrors.itemName = 'Item name is required';
      }
      if (!currentFormData.category) {
        newErrors.category = 'Category is required';
      }
      if (!currentFormData.type) {
        newErrors.type = 'Type is required';
      }
      if (!currentFormData.unitType) {
        newErrors.unitType = 'Unit type is required';
      }

      // Validate pricing based on pricing type
      if (currentFormData.pricingType === 'basePrice') {
        if (!currentFormData.basePrice) {
          newErrors.basePrice = 'Base price is required';
        } else if (
          isNaN(currentFormData.basePrice) ||
          currentFormData.basePrice <= 0
        ) {
          newErrors.basePrice = 'Base price must be a valid positive number';
        }
      } else if (currentFormData.pricingType === 'mrp') {
        if (!currentFormData.mrp) {
          newErrors.mrp = 'MRP is required';
        } else if (isNaN(currentFormData.mrp) || currentFormData.mrp <= 0) {
          newErrors.mrp = 'MRP must be a valid positive number';
        }
        if (!currentFormData.sellingPrice) {
          newErrors.sellingPrice = 'Selling price is required';
        } else if (
          isNaN(currentFormData.sellingPrice) ||
          currentFormData.sellingPrice <= 0
        ) {
          newErrors.sellingPrice = 'Selling price must be a valid positive number';
        } else if (
          parseFloat(currentFormData.sellingPrice) >
          parseFloat(currentFormData.mrp)
        ) {
          newErrors.sellingPrice = 'Selling price cannot be greater than MRP';
        }
      }

      // Validate variants
      if (currentFormData.variants?.length) {
        const variantErrors = [];
        currentFormData.variants.forEach((variant, index) => {
          const error = {};
          if (!variant.name?.trim()) {
            error.name = 'Variant name is required';
          }
          if (!variant.price) {
            error.price = 'Price is required';
          } else if (isNaN(variant.price) || variant.price <= 0) {
            error.price = 'Price must be a valid positive number';
          }
          if (Object.keys(error).length > 0) {
            variantErrors[index] = error;
          }
        });
        if (variantErrors.length) {
          newErrors.variants = variantErrors;
        }
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const updateBasePriceFromVariants = (variants) => {
      if (variants && variants.length > 0) {
        const prices = variants
          .map((v) => parseFloat(v.price))
          .filter((p) => !isNaN(p));
        if (prices.length > 0) {
          const minPrice = Math.min(...prices);
          setCurrentFormData((prev) => ({
            ...prev,
            basePrice: minPrice.toString()
          }));
        }
      }
    };

    const handleSubmit = () => {
      if (validateForm()) {
        const formattedData = {
          ...currentFormData,
          pricingType: currentFormData.pricingType,
          ...(currentFormData.pricingType === 'basePrice' && {
            basePrice: parseFloat(currentFormData.basePrice)
          }),
          ...(currentFormData.pricingType === 'mrp' && {
            mrp: parseFloat(currentFormData.mrp),
            sellingPrice: parseFloat(currentFormData.sellingPrice)
          }),
          variants: currentFormData.variants.map((variant) => ({
            ...variant,
            price: parseFloat(variant.price)
          }))
        };
        handleFormSubmit(formattedData);
      }
    };

    const handleVariantChange = (index, field, value) => {
      const updatedVariants = [...currentFormData.variants];
      updatedVariants[index] = { ...updatedVariants[index], [field]: value };
      setCurrentFormData((prev) => ({ ...prev, variants: updatedVariants }));
      if (field === 'price') {
        updateBasePriceFromVariants(updatedVariants);
      }
    };

    const handleUnitTypeSelection = (selectedType) => {
      setCurrentFormData((prev) => ({
        ...prev,
        unitType: selectedType,
        variants: unitTypes[selectedType].map((variant) => ({
          name: variant,
          price: ''
        }))
      }));
    };

    const addVariant = () => {
      setCurrentFormData((prev) => ({
        ...prev,
        variants: [...prev.variants, { name: '', price: '' }]
      }));
    };

    const removeVariant = (index) => {
      const updatedVariants = currentFormData.variants.filter((_, i) => i !== index);
      setCurrentFormData((prev) => ({ ...prev, variants: updatedVariants }));
      updateBasePriceFromVariants(updatedVariants);
    };

    return (
      <div className="form-container">
        <div className="modal-header">
          <h2>{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
          <button className="close-button" onClick={resetForm}>
            Close
          </button>
        </div>
        <div className="form-scroll">
          {/* Image Upload */}
          <div className="image-upload">
            {selectedImage ? (
              <img src={selectedImage.uri} alt="Preview" className="image-preview" />
            ) : (
              <label className="image-placeholder">
                Tap to upload image
                <input type="file" accept="image/*" onChange={handleImagePick} />
              </label>
            )}
          </div>

          {/* Item Name */}
          <div className="form-group">
            <label className="label">Item Name *</label>
            <input
              className={`input ${errors.itemName ? 'input-error' : ''}`}
              value={currentFormData.itemName}
              onChange={(e) =>
                setCurrentFormData((prev) => ({ ...prev, itemName: e.target.value }))
              }
              placeholder="Enter item name"
            />
            {errors.itemName && <p className="error-text">{errors.itemName}</p>}
          </div>

          {/* Pricing Type */}
          <div className="form-group">
            <label className="label">Pricing Type *</label>
            <div className="pricing-type-buttons">
              <button
                className={`pricing-type-button ${
                  currentFormData.pricingType === 'basePrice' ? 'active' : ''
                }`}
                onClick={() =>
                  setCurrentFormData((prev) => ({ ...prev, pricingType: 'basePrice' }))
                }
              >
                Base Price
              </button>
              <button
                className={`pricing-type-button ${
                  currentFormData.pricingType === 'mrp' ? 'active' : ''
                }`}
                onClick={() =>
                  setCurrentFormData((prev) => ({ ...prev, pricingType: 'mrp' }))
                }
              >
                MRP Based
              </button>
            </div>
          </div>

          {currentFormData.pricingType === 'basePrice' ? (
            <div className="form-group">
              <label className="label">Base Price *</label>
              <input
                className={`input ${errors.basePrice ? 'input-error' : ''}`}
                value={currentFormData.basePrice}
                onChange={(e) =>
                  setCurrentFormData((prev) => ({ ...prev, basePrice: e.target.value }))
                }
                placeholder="Enter base price"
                type="number"
              />
              {errors.basePrice && <p className="error-text">{errors.basePrice}</p>}
            </div>
          ) : (
            <>
              <div className="form-group">
                <label className="label">MRP *</label>
                <input
                  className={`input ${errors.mrp ? 'input-error' : ''}`}
                  value={currentFormData.mrp}
                  onChange={(e) =>
                    setCurrentFormData((prev) => ({ ...prev, mrp: e.target.value }))
                  }
                  placeholder="Enter MRP"
                  type="number"
                />
                {errors.mrp && <p className="error-text">{errors.mrp}</p>}
              </div>
              <div className="form-group">
                <label className="label">Selling Price *</label>
                <input
                  className={`input ${errors.sellingPrice ? 'input-error' : ''}`}
                  value={currentFormData.sellingPrice}
                  onChange={(e) =>
                    setCurrentFormData((prev) => ({
                      ...prev,
                      sellingPrice: e.target.value
                    }))
                  }
                  placeholder="Enter selling price"
                  type="number"
                />
                {errors.sellingPrice && (
                  <p className="error-text">{errors.sellingPrice}</p>
                )}
              </div>
            </>
          )}

          {/* Category Selection */}
          <div className="form-group">
            <label className="label">Category *</label>
            <div className="category-buttons-container">
              {['Veg', 'Non-Veg'].map((cat) => (
                <button
                  key={cat}
                  className={`category-button ${
                    currentFormData.category === cat ? 'active' : ''
                  } ${cat === 'Veg' ? 'veg' : 'non-veg'}`}
                  onClick={() =>
                    setCurrentFormData((prev) => ({ ...prev, category: cat }))
                  }
                >
                  {cat}
                </button>
              ))}
            </div>
            {errors.category && <p className="error-text">{errors.category}</p>}
          </div>

          {/* Type Selection */}
          <div className="form-group">
            <label className="label">Type *</label>
            <div className="type-buttons">
              {typeOptions.map((type) => (
                <button
                  key={type}
                  className={`type-button ${
                    currentFormData.type === type ? 'active' : ''
                  }`}
                  onClick={() =>
                    setCurrentFormData((prev) => ({ ...prev, type }))
                  }
                >
                  {type}
                </button>
              ))}
            </div>
            {errors.type && <p className="error-text">{errors.type}</p>}
          </div>

          {/* Unit Type Selection */}
          <div className="form-group">
            <label className="label">Unit Type *</label>
            <div className="unit-type-buttons">
              {Object.keys(unitTypes).map((type) => (
                <button
                  key={type}
                  className={`unit-type-button ${
                    currentFormData.unitType === type ? 'active' : ''
                  }`}
                  onClick={() => handleUnitTypeSelection(type)}
                >
                  {type}
                </button>
              ))}
            </div>
            {errors.unitType && <p className="error-text">{errors.unitType}</p>}
          </div>

          {/* Variants Section */}
          <div className="variants-section">
            <div className="variants-header">
              <h3>Variants</h3>
              <button className="add-variant-button" onClick={addVariant}>
                Add Variant
              </button>
            </div>
            {currentFormData.variants.map((variant, index) => (
              <div key={index} className="variant-form">
                <div className="variant-inputs">
                  <input
                    className={`variant-input ${
                      errors.variants?.[index]?.name ? 'input-error' : ''
                    }`}
                    value={variant.name}
                    onChange={(e) =>
                      handleVariantChange(index, 'name', e.target.value)
                    }
                    placeholder="Variant name"
                  />
                  <input
                    className={`variant-input ${
                      errors.variants?.[index]?.price ? 'input-error' : ''
                    }`}
                    value={variant.price}
                    onChange={(e) =>
                      handleVariantChange(index, 'price', e.target.value)
                    }
                    placeholder="Price"
                    type="number"
                  />
                  <button
                    className="remove-variant-button"
                    onClick={() => removeVariant(index)}
                  >
                    Remove
                  </button>
                </div>
                {errors.variants?.[index] && (
                  <div className="variant-errors">
                    {Object.entries(errors.variants[index]).map(([key, err]) => (
                      <p key={key} className="error-text">
                        {err}
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Form Buttons */}
          <div className="button-container">
            <button className="secondary-button" onClick={resetForm}>
              Cancel
            </button>
            <button className="primary-button" onClick={handleSubmit}>
              {editingProduct ? 'Update' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // --- Product Card Component ---
  const ProductCard = React.memo(({ product }) => (
    <div className="product-card">
      <div className="image-container">
        {product.imageUrl ? (
          <img src={product.imageUrl} alt="Product" className="product-image" />
        ) : (
          <div className="placeholder-image">No Image</div>
        )}
        <div
          className={`category-badge ${
            product.category === 'Veg' ? 'veg' : 'non-veg'
          }`}
        >
          {product.category}
        </div>
        {product.outOfStock && <div className="out-of-stock-overlay">Out of Stock</div>}
      </div>
      <div className="product-details">
        <h3 className="product-name">{product.itemName}</h3>
        <p className="product-type">{product.type}</p>
        {product.pricingType === 'basePrice' ? (
          <p className="base-price">Base Price: ₹{product.basePrice}</p>
        ) : (
          <p className="selling-price">Selling Price: ₹{product.sellingPrice}</p>
        )}
        <div className="variants-container">
          <p className="variants-title">Variants:</p>
          {product.variants.map((variant, idx) => (
            <div key={idx} className="variant-row">
              <span className="variant-name">{variant.name}</span>
              <span className="variant-price">₹{variant.price}</span>
            </div>
          ))}
        </div>
        <div className="action-buttons-container">
          <div className="button-row">
            <button
              className="action-button edit-button"
              onClick={() => handleEdit(product)}
            >
              Edit
            </button>
            <button
              className="action-button delete-button"
              onClick={() => handleDelete(product._id)}
            >
              Delete
            </button>
          </div>
          <button
            className="action-button in-stock-button"
            onClick={() => handleStockUpdate(product._id, !product.inStock)}
          >
            {product.inStock ? 'In Stock' : 'Out of Stock'}
          </button>
        </div>
      </div>
    </div>
  ));

  // --- Filtered and Grouped Products ---
  const filteredProducts = useMemo(() => {
    return products
      .filter((product) => {
        const matchesSearch =
          searchQuery === '' ||
          product.itemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
          product.type.toLowerCase().includes(searchQuery.toLowerCase());
        const matchesStock =
          stockFilter === 'all' ||
          (stockFilter === 'inStock' && product.inStock) ||
          (stockFilter === 'outOfStock' && !product.inStock);
        const matchesType =
          typeFilter === 'all' || product.type === typeFilter;
        return matchesSearch && matchesStock && matchesType;
      })
      .reduce((acc, product) => {
        if (!acc[product.type]) acc[product.type] = [];
        acc[product.type].push(product);
        return acc;
      }, {});
  }, [products, searchQuery, stockFilter, typeFilter]);

  const renderProductGrid = (productsArr) => {
    // Standard responsive grid (no horizontal scroll)
    return (
      <div className="product-grid-container">
        {productsArr.map((product) => (
          <ProductCard
            key={product._id}
            product={{ ...product, outOfStock: !product.inStock }}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="page-container">
      <ToastContainer position="top-right" autoClose={5000} />
      {/* Header */}
      <div className="header-bar">
        <h1 className="page-title">Restaurant's Product</h1>
        <button className="add-button" onClick={() => setIsFormVisible(true)}>
          Add New Item
        </button>
      </div>

      {/* Filters */}
      <div className="filter-container">
        <input
          className="search-input"
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div className="filter-buttons">
          <button
            className={`filter-button ${stockFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStockFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-button ${
              stockFilter === 'inStock' ? 'active' : ''
            }`}
            onClick={() => setStockFilter('inStock')}
          >
            In Stock
          </button>
          <button
            className={`filter-button ${
              stockFilter === 'outOfStock' ? 'active' : ''
            }`}
            onClick={() => setStockFilter('outOfStock')}
          >
            Out of Stock
          </button>
        </div>
      </div>

      {/* Product Listing */}
      <div className="product-section">
        {loading ? (
          <div className="loader">Loading...</div>
        ) : (
          Object.entries(filteredProducts).map(([type, typeProducts]) => (
            <div key={type} className="category-block">
              <h2 className="type-header">{type.toUpperCase()}</h2>
              {renderProductGrid(typeProducts)}
            </div>
          ))
        )}
      </div>

      {/* Modal for Product Form */}
      {isFormVisible && (
        <div className="modal">
          <ProductForm />
        </div>
      )}
    </div>
  );
};

export default Product;
